import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http'
import { inject } from '@angular/core'
import { AuthService } from '@core/services/api'
import { catchError, mergeMap, of, switchMap, throwError } from 'rxjs'
import { Router } from '@angular/router'
import { onboardingPath, OnboardingStep } from '@/app/utils/onboarding'
import { environment } from '@/environments/environment'

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService)
  const router = inject(Router)
  const isPublicAuthEndpoint = auth.isPublicAuthEndpoint(req.url)
  const isAuthEndpoint = auth.isAuthEndpoint(req.url)
  const token = auth.getToken()
  // Only our own API gets the token; a request to any other origin must never carry it.
  const isApiRequest = req.url.startsWith(`${environment.apiUrls.replace(/\/+$/, '')}/`)

  const handleInactiveUser = (payload?: { resultCode?: string; developerMessage?: string } | null) => {
    const resultCode = String(payload?.resultCode ?? '')
    const developerMessage = String(payload?.developerMessage ?? '').toLowerCase()
    const isInactiveUser = developerMessage.includes('inactive')

    if (resultCode === '40300' && isInactiveUser) {
      auth.clearSession()
    }
  }

  const withBearer = (bearer: string) =>
    req.clone({ setHeaders: { Authorization: `Bearer ${bearer}` } })

  const authReq =
    token && isApiRequest && !isPublicAuthEndpoint && !req.headers.has('Authorization')
      ? withBearer(token)
      : req

  return next(authReq).pipe(
    mergeMap((event) => {
      if (event instanceof HttpResponse) {
        const body = (event.body ?? null) as
          | { resultCode?: string; developerMessage?: string }
          | null
        handleInactiveUser(body)
      }

      return of(event)
    }),
    catchError((error: unknown) => {
      const httpError = error instanceof HttpErrorResponse ? error : null

      const errorBody = (httpError?.error ?? null) as
        | {
            resultCode?: string
            developerMessage?: string
            resultData?: { onboarding?: OnboardingStep }
          }
        | null

      handleInactiveUser(errorBody)
      const onboarding = errorBody?.resultData?.onboarding
      if (httpError?.status === 403 && onboarding) {
        void router.navigate([onboardingPath(onboarding)], {
          queryParams: { returnUrl: router.url },
        })
        return throwError(() => error)
      }

      if (httpError?.status !== 401 || isAuthEndpoint || !isApiRequest) {
        return throwError(() => error)
      }

      // Keepalive / another tab already rotated — retry with the new JWT
      // instead of burning the refresh token again (that race logs users out).
      const latest = auth.getToken()
      if (latest && latest !== token) {
        return next(withBearer(latest))
      }

      if (!auth.getRefreshToken()) {
        return throwError(() => error)
      }

      return auth.refreshToken().pipe(
        switchMap((newToken) => next(withBearer(newToken))),
        catchError((refreshError) => {
          const afterRefresh = auth.getToken()
          if (afterRefresh && afterRefresh !== token) {
            return next(withBearer(afterRefresh))
          }
          auth.clearSession()
          return throwError(() => refreshError)
        })
      )
    })
  )
}
